import { useState, useEffect, type FormEvent } from 'react'
import { toast } from 'sonner'
import { Button } from '../../../shared/components/ui/Button.js'
import { Input } from '../../../shared/components/ui/Input.js'
import { Modal } from '../../../shared/components/ui/Modal.js'
import { api } from './api.js'
import { ErrorBox } from './ErrorBox.js'
import { EMPTY_CONTA_FORM, type Conta, type ContaFormData, type ContaPlan } from './types.js'

interface Props {
  open: boolean
  onClose: () => void
  onSaved: () => void
  conta: Conta
}

export function EditarContaModal({ open, onClose, onSaved, conta }: Props) {
  const [form, setForm] = useState<ContaFormData>(EMPTY_CONTA_FORM)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (open) {
      setForm({
        nome: conta.nome,
        email: conta.email,
        telefone: conta.telefone ?? '',
        plan: conta.plan,
        status: conta.status,
      })
      setError('')
    }
  }, [open, conta])

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setLoading(true)
    setError('')
    try {
      await api.patch(`/api/v1/super/contas/${conta.id}`, {
        nome: form.nome,
        email: form.email,
        telefone: form.telefone || null,
        plan: form.plan,
      })
      onSaved()
      onClose()
      toast.success('Conta atualizada')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro inesperado'
      setError(msg)
      toast.error('Erro ao atualizar', { description: msg })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Editar conta" size="md">
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 mt-2">
        <Input
          label="Nome do responsável"
          value={form.nome}
          onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
          autoFocus
        />
        <Input
          label="E-mail"
          type="email"
          value={form.email}
          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
        />
        <Input
          label="Telefone"
          value={form.telefone}
          onChange={(e) => setForm((f) => ({ ...f, telefone: e.target.value }))}
          inputMode="tel"
        />
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-[#374151]">Plano</label>
          <select
            value={form.plan}
            onChange={(e) => setForm((f) => ({ ...f, plan: e.target.value as ContaPlan }))}
            className="h-10 rounded-lg border border-[#d1d5db] px-3 text-sm text-[#0f172a] bg-white focus:outline-none focus:ring-2 focus:ring-[#0a4ee3] focus:border-transparent"
          >
            <option value="starter">Starter</option>
            <option value="pro">Pro</option>
            <option value="enterprise">Enterprise</option>
          </select>
        </div>
        {error && <ErrorBox message={error} />}
        <div className="flex justify-end gap-3 mt-2 pt-4 border-t border-[#f1f5f9]">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" loading={loading}>
            Salvar alterações
          </Button>
        </div>
      </form>
    </Modal>
  )
}
